/**
 * The numbers behind the How-it-works page (`ui/guide-page.ts`), and nothing else.
 *
 * Blockari's `screens/guide-data.ts`, for 21 Wild: rules live in `@/game/*` and only there. Every figure
 * here is read from those constants or produced by running the game's own functions (`resolvePile`,
 * `applyOutcome`, `timeBonus`, `finalScore`, `awardForRound`, …), so this page cannot drift from the game.
 * `guide-data.test.ts` re-derives each table independently.
 */

import {
  CHEST_TABLE,
  LEADERBOARD_MIN_COMBOS,
  LEADERBOARD_MIN_GAMES,
  LOGIN_STREAK,
  PPM,
  RARITIES,
  WEEKLY_TOP_PLACES,
  WIN_MILESTONES,
  chestOdds,
  finalOdds,
  streakChestAt,
  type Rarity,
} from '@/game/chests';
import { levelThreshold } from '@/game/levels';
import { FIVE_CARD_COUNT, TARGET, resolvePile, totalsOf } from '@/game/rules';
import { applyOutcome, finalScore, initialScoreState, streakBonusOf, timeBonus } from '@/game/scoring';
import { MAX_BUSTS, POINTS, ROUND_SECONDS, STREAK_LABELS, type CardId } from '@/game/types';
import { RANK_NAMES, RANK_SPAN, XP_RATES, awardForRound, awardForWin, totalOf, xpThreshold } from '@/game/xp';
import type { LeaderboardBoard } from '@/net/api';

import { BOARDS } from './leaderboard-data';

export { RANK_NAMES, RANK_SPAN, ROUND_SECONDS, MAX_BUSTS, TARGET, FIVE_CARD_COUNT, LOGIN_STREAK, RARITIES };

/* --------------------------------------------------------------------------
   Rules and scoring
   -------------------------------------------------------------------------- */

export interface PointRow {
  readonly label: string;
  readonly points: number;
  readonly note: string;
}

/** What a pile that clears pays, before any streak. Straight from `POINTS`. */
export function pileRows(): readonly PointRow[] {
  return [
    { label: 'Twenty-one', points: POINTS.twentyOne, note: `A pile that totals exactly ${TARGET} clears.` },
    { label: 'Five cards', points: POINTS.fiveCard, note: `A fifth card on a pile still ${TARGET} or under clears it.` },
    { label: 'Wild', points: POINTS.wild, note: 'The J♠ and J♣ clear any pile they land on, and cancel a bust.' },
  ];
}

export interface PileExample {
  readonly pile: readonly CardId[];
  readonly last: CardId;
  readonly total: string;
  readonly says: string;
  readonly points: number;
  readonly cleared: boolean;
  readonly bust: boolean;
}

/** A card played onto a pile, resolved by the real rules. */
export function pileExample(pile: readonly CardId[], last: CardId, says: string): PileExample {
  const o = resolvePile(pile, last);
  const t = o.totals;
  const total = t.soft !== t.hard && t.soft <= TARGET ? `${t.hard} / ${t.soft}` : String(t.hard);
  return { pile, last, total, says, points: o.basePoints, cleared: o.cleared, bust: o.isBust };
}

/** The handful of plays that show every way a pile ends. */
export function pileExamples(): readonly PileExample[] {
  return [
    pileExample(['10h', '5d'], '6c', 'Ten, five and six make 21: clears for 400.'),
    pileExample(['ah'], 'kd', 'An ace counts 11 or 1, whichever helps: ace + king is 21.'),
    pileExample(['2h', '3d', '4c', '2s'], '3h', 'Five cards at 14: the five-card bonus, 600.'),
    pileExample(['5h', '4d', '6c', '3s'], '3d', 'A fifth card that lands on 21 pays both: 400 + 600.'),
    pileExample(['kh', 'qd'], '5c', 'Over 21 is a bust: the pile clears for nothing, and counts toward your three.'),
    pileExample(['kh', 'qd'], 'js', 'A wild saves it: the bust is cancelled and the pile clears for 200.'),
  ];
}

export interface StreakRow {
  readonly level: number;
  readonly label: string;
  /** Scoring plays in a row it takes to reach this level. */
  readonly inARow: number;
  readonly bonus: number;
}

/** The streak ladder: every consecutive scoring play steps it up; a play that scores nothing resets it. */
export function streakRows(): readonly StreakRow[] {
  return STREAK_LABELS.map((label, i) => ({ level: i + 1, label, inARow: i + 2, bonus: streakBonusOf(i + 1) }));
}

/** Prove the ladder by playing it: n scoring plays in a row through the real scorer. */
export function playedStreak(plays: number): { total: number; lastLabel: string | null } {
  let state = initialScoreState();
  let label: string | null = null;
  const outcome = resolvePile(['10h', '5d'], '6c');
  for (let i = 0; i < plays; i++) {
    const r = applyOutcome(state, outcome);
    state = r.state;
    label = r.streakLabel;
  }
  return { total: state.score, lastLabel: label };
}

export interface TimeRow {
  readonly seconds: number;
  readonly cards: number;
  readonly bonus: number;
}

/** The end-of-round time bonus: seconds left x (10 - 0.2 per card left unplayed). */
export function timeRows(): readonly TimeRow[] {
  return [
    { seconds: 60, cards: 0, bonus: 0 },
    { seconds: 60, cards: 10, bonus: 0 },
    { seconds: 90, cards: 20, bonus: 0 },
    { seconds: 120, cards: 5, bonus: 0 },
    { seconds: 30, cards: 0, bonus: 0 },
  ].map((r) => ({ ...r, bonus: timeBonus(r.seconds, r.cards) }));
}

export const END_BONUSES = [
  { label: 'Perfect game', points: POINTS.perfectGame, note: 'Every card played: the stock and all four piles empty.' },
  { label: 'No bust', points: POINTS.noBust, note: 'Finish without a single bust.' },
] as const;

export interface LevelRow {
  readonly level: number;
  readonly score: number;
}

/** In-match levels: cosmetic, paid by nothing. The score each is reached at. */
export function levelRows(max = 10): readonly LevelRow[] {
  return Array.from({ length: max - 1 }, (_, i) => ({ level: i + 2, score: levelThreshold(i + 2) }));
}

/* --------------------------------------------------------------------------
   XP and ranks
   -------------------------------------------------------------------------- */

export interface XpPartRow {
  readonly label: string;
  readonly rule: string;
  readonly rate: number;
}

export function xpPartRows(): readonly XpPartRow[] {
  return [
    { label: 'Played', rule: `${XP_RATES.played} for finishing a round (at least one card played)`, rate: XP_RATES.played },
    { label: 'Score', rule: `1 per ${XP_RATES.scorePer} points of final score`, rate: 1 },
    { label: 'Clears', rule: `${XP_RATES.clear} per pile cleared`, rate: XP_RATES.clear },
    { label: 'Levels', rule: `${XP_RATES.perLevel} per match level beyond the first`, rate: XP_RATES.perLevel },
    { label: 'Streak', rule: `${XP_RATES.streak} once, for reaching ${STREAK_LABELS[XP_RATES.streakAt - 1]} or more`, rate: XP_RATES.streak },
    { label: 'Challenge', rule: `${XP_RATES.challenge} for playing a challenge at all, win or lose`, rate: XP_RATES.challenge },
    { label: 'Win', rule: `${XP_RATES.win} to the winner of a challenge, plus half the pot (the pot counts up to ${XP_RATES.potCap}, so a bigger wager earns no more XP)`, rate: XP_RATES.win },
  ];
}

export interface XpExample {
  readonly round: { score: number; clears: number; level: number; bestStreak: number };
  readonly parts: ReturnType<typeof awardForRound>;
  readonly total: number;
  readonly win: number;
}

/** A round worked through the real award. */
export function xpExample(): XpExample {
  const round = { score: 8000, clears: 14, level: 8, bestStreak: 3 };
  const parts = awardForRound({ ...round, plays: 40, challenge: true });
  return { round, parts, total: totalOf(parts), win: totalOf(awardForWin(100)) };
}

export interface RankRow {
  readonly name: string;
  readonly fromLevel: number;
  readonly fromXp: number;
}

/** Nine named ranks, five XP levels each. */
export function rankRows(): readonly RankRow[] {
  return RANK_NAMES.map((name, i) => ({ name, fromLevel: i * RANK_SPAN + 1, fromXp: xpThreshold(i * RANK_SPAN + 1) }));
}

export interface XpLevelRow {
  readonly level: number;
  readonly xp: number;
  readonly cost: number;
}

export function xpLevelRows(max = 10): readonly XpLevelRow[] {
  return Array.from({ length: max - 1 }, (_, i) => {
    const level = i + 2;
    return { level, xp: xpThreshold(level), cost: xpThreshold(level) - xpThreshold(level - 1) };
  });
}

/* --------------------------------------------------------------------------
   Chests
   -------------------------------------------------------------------------- */

export const RARITY_WORD: Record<Rarity, string> = { common: 'Common', rare: 'Rare', epic: 'Epic', legendary: 'Legendary' };

/** One starting rarity on a source card, with the small tag ("1st", "from Epic") that sits before it. */
export interface SourceStart {
  readonly label: string | null;
  readonly rarity: string;
}

export interface SourceRow {
  readonly label: string;
  readonly when: string;
  readonly starts: readonly SourceStart[];
}

const ORDINAL = ['1st', '2nd', '3rd'] as const;

/**
 * The boards whose ranking query applies MIN_GAMES (server/src/leaderboard.ts: `played()` for $CHAIN and Best score, the
 * `HAVING count(*) >= MIN_GAMES` qualifier of Win streaks). XP has no minimum; Card streaks counts MIN_COMBOS rounds
 * with a 2X or better streak instead.
 */
const MIN_GAMES_BOARDS: readonly LeaderboardBoard[] = ['won', 'score', 'streak'];

/** "A, B and C" (or "A, B or C") from board pill labels. */
const labelsOf = (boards: readonly LeaderboardBoard[], conj = 'and'): string => {
  const names = boards.map((b) => BOARDS.find((d) => d.value === b)?.label ?? b);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} ${conj} ${names[names.length - 1]}` : (names[0] ?? '');
};

/** The weekly top-3 row's text: every paid board, when it pays, who is eligible and each board's own minimum. */
export function weeklyTopWhen(): string {
  const paid = BOARDS.filter((b) => b.paid).map((b) => b.value);
  return (
    `Finish the week in the top ${WEEKLY_TOP_PLACES} of a weekly board (${labelsOf(paid, 'or')}: each board pays its own top ${WEEKLY_TOP_PLACES}). ` +
    `Paid when the week closes, Monday 00:00 UTC. Needs a signed-in account and a positive total. ` +
    `${labelsOf(MIN_GAMES_BOARDS)} also need ${LEADERBOARD_MIN_GAMES} games played that week; ` +
    `${labelsOf(['combo'])} needs ${LEADERBOARD_MIN_COMBOS} games with a 2X+ streak.`
  );
}

/** Every way to earn a chest, in Blockari's order: rank up, the win milestones, streaks, weekly, welcome. */
export function sourceRows(): readonly SourceRow[] {
  const start = CHEST_TABLE.start;
  const rows: SourceRow[] = [
    {
      label: 'Rank up',
      when: 'Every time you reach a new rank.',
      starts: [...CHEST_TABLE.rankUpStart].reverse().map((b) => ({ label: `from ${RANK_NAMES[b.fromRank]}`, rarity: RARITY_WORD[b.rarity] })),
    },
    { label: 'First win', when: 'Your first challenge win.', starts: [{ label: null, rarity: RARITY_WORD[start.first_win] }] },
  ];
  for (const m of WIN_MILESTONES.filter((w) => w.wins > 1)) {
    rows.push({ label: `${m.wins} wins`, when: `Your ${m.wins}th challenge win, lifetime.`, starts: [{ label: null, rarity: RARITY_WORD[start[m.source as 'wins_10']] }] });
  }
  rows.push(
    { label: 'Win streak', when: `Challenge wins in a row: a chest at ${streakRuns(8).join(', ')}, then every 5 after. A loss starts the count again.`, starts: [{ label: null, rarity: RARITY_WORD[start.win_streak_3] }] },
    { label: `${LOGIN_STREAK}-day streak`, when: `Every ${LOGIN_STREAK}th day in a row you collect the daily top-up.`, starts: [{ label: null, rarity: RARITY_WORD[start.login_streak_7] }] },
    {
      label: 'Weekly top 3',
      when: weeklyTopWhen(),
      starts: CHEST_TABLE.weeklyTopStart.map((r, i) => ({ label: ORDINAL[i]!, rarity: RARITY_WORD[r] })),
    },
    { label: 'Welcome', when: 'A gift when you create your account.', starts: [{ label: null, rarity: RARITY_WORD[start.welcome] }] },
  );
  return rows;
}

/** The run lengths that earn a streak chest, first `count`. */
export function streakRuns(count: number): number[] {
  const out: number[] = [];
  for (let n = 1; out.length < count; n++) if (streakChestAt(n)) out.push(n);
  return out;
}

export interface ContentsRow {
  readonly rarity: Rarity;
  readonly chain: string;
  readonly ticket: string;
  readonly boost: string;
  readonly cosmetic: string;
}

const pct = (bp: number): string => `${Math.round(bp / 100)}%`;

/** What each rarity can hold: $CHAIN always, the rest by chance. */
export function contentsRows(): readonly ContentsRow[] {
  return RARITIES.map((rarity) => {
    const c = CHEST_TABLE.contents[rarity];
    return {
      rarity,
      chain: `${c.chain.min}–${c.chain.max} $CHAIN`,
      ticket: `${pct(c.ticket.chanceBp)} · ${c.ticket.fee} ticket`,
      boost: `${pct(c.boost.chanceBp)} · ${c.boost.games} games`,
      cosmetic: pct(c.cosmetic.chanceBp),
    };
  });
}

export interface UpgradeRow {
  readonly from: Rarity;
  readonly to: Rarity;
  readonly chance: string;
}

/** Each tap on an arriving chest can upgrade it one step; the first miss stops it. */
export function upgradeRows(): readonly UpgradeRow[] {
  return chestOdds().upgradeBp.map((u) => ({ from: u.from, to: u.to, chance: pct(u.bp) }));
}

/** Final rarity odds for a chest that starts at `start`, whole percent, from the published table. */
export function finalOddsRow(start: Rarity): Record<Rarity, string> {
  const ppm = finalOdds(start);
  const out = {} as Record<Rarity, string>;
  for (const r of RARITIES) out[r] = `${Number(((ppm[r] / PPM) * 100).toFixed(4))}%`; // full precision, as Blockari's pct()
  return out;
}

export const BOOST = {
  multiplier: CHEST_TABLE.boostMultiplier,
  gamesByRarity: RARITIES.map((r) => ({ rarity: r, games: CHEST_TABLE.contents[r].boost.games })),
} as const;

/** `totalsOf`, re-exported for the page's inline pile sums. */
export { totalsOf };


/* --------------------------------------------------------------------------
   Worked examples and the ranks' chests
   -------------------------------------------------------------------------- */

export interface ScoreExample {
  readonly rows: ReadonlyArray<{ what: string; points: number }>;
  readonly base: number;
  readonly noBust: number;
  readonly time: number;
  readonly seconds: number;
  readonly cardsLeft: number;
  readonly total: number;
}

/** A short round played through the real scorer, then closed with the real end bonuses. */
export function scoreExample(): ScoreExample {
  const plays: ReadonlyArray<[string, CardId[], CardId]> = [
    ['Ten, five and six make 21', ['10h', '5d'], '6c'],
    ['A fifth card at 14', ['2h', '3d', '4c', '2s'], '3h'],
    ['A wild saves a bust', ['kh', 'qd'], 'js'],
  ];
  let state = initialScoreState();
  const rows: Array<{ what: string; points: number }> = [];
  for (const [what, pile, last] of plays) {
    const before = state.score;
    const r = applyOutcome(state, resolvePile(pile, last));
    state = r.state;
    rows.push({ what: r.streakLabel ? `${what} \u00b7 streak ${r.streakLabel}` : what, points: state.score - before });
  }
  const seconds = 40;
  const cardsLeft = 20;
  const fin = finalScore({
    score: state.score,
    busts: state.busts,
    clears: state.clears,
    bestStreak: state.bestStreak,
    plays: state.plays,
    secondsRemaining: seconds,
    cardsRemaining: cardsLeft,
    perfectGame: false,
    reason: 'timeout',
  });
  return { rows, base: state.score, noBust: fin.noBustBonus, time: fin.timeBonus, seconds, cardsLeft, total: fin.total };
}

/** The chest a rank-up pays at rank `index` (the table's first rarity whose `fromRank` it reaches, from the top). */
export function rankChest(index: number): Rarity {
  const hit = [...CHEST_TABLE.rankUpStart].reverse().find((b) => index >= b.fromRank);
  return hit?.rarity ?? CHEST_TABLE.rankUpStart[0]!.rarity;
}

/** A table cell with a chance on top and a small second line under it ("25%" over "\u00b7 5"). */
export interface StackedCell {
  readonly main: string;
  readonly small: string;
}

/** The chest table's raw contents, for the guide's rarity-by-column matrix. */
export function contentsTable(): ReadonlyArray<{ rarity: Rarity; chain: string; ticket: StackedCell; boost: StackedCell; cosmetic: string }> {
  return RARITIES.map((rarity) => {
    const c = CHEST_TABLE.contents[rarity];
    const bp = (n: number): string => `${Math.round(n / 100)}%`;
    return {
      rarity,
      chain: `${c.chain.min}\u2013${c.chain.max}`,
      ticket: { main: bp(c.ticket.chanceBp), small: `\u00b7 ${c.ticket.fee}` },
      boost: { main: bp(c.boost.chanceBp), small: `\u00b7 ${c.boost.games}` },
      cosmetic: bp(c.cosmetic.chanceBp),
    };
  });
}
